import { useMemo, useRef, useState } from "react";
import { Finance } from "../lib/api";
import { toast } from "sonner";
import { Upload, X, CheckCircle2, AlertTriangle, FileSpreadsheet, Trash2 } from "lucide-react";

/**
 * Modal for importing invoices from an .xlsx ledger (KSeF / accountant export).
 *
 * Flow:
 *  1. Admin uploads a file → backend parses & returns rows with suggested type/category
 *  2. Admin reviews the table, edits category / vehicle / employee / include-flag
 *  3. Admin clicks "Zaimportuj" → we send the finalized rows to /finance/import/commit
 */
export default function FinanceImportModal({ open, onClose, meta, fleet, employees, onImported }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileInputRef = useRef(null);

  const included = useMemo(() => rows.filter((r) => r._include), [rows]);
  const totalGross = useMemo(
    () => included.reduce((s, r) => s + (Number(r.gross_amount) || 0), 0),
    [included]
  );

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setRows([]);
    setParsing(false);
    setCommitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Plik musi być formatu .xlsx");
      return;
    }
    setFile(f);
    setParsing(true);
    try {
      const data = await Finance.importParse(f);
      const initial = data.rows.map((r) => ({
        ...r,
        _include: true,
        type: r.suggested_type,
        category: r.suggested_category,
        vehicle_id: "",
        employee_id: "",
      }));
      setRows(initial);
      toast.success(`Rozpoznano ${data.count} wierszy`);
    } catch (err) {
      toast.error("Błąd parsowania: " + (err.response?.data?.detail || err.message));
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const commit = async () => {
    if (included.length === 0) {
      toast.error("Zaznacz przynajmniej jeden wiersz");
      return;
    }
    // Validate categorization
    const missing = included.filter((r) => !r.category);
    if (missing.length > 0) {
      toast.error(`Brak kategorii w ${missing.length} wierszach`);
      return;
    }
    const missingVehicle = included.filter(
      (r) => r.type === "expense" && meta.categories_with_vehicle.includes(r.category) && !r.vehicle_id
    );
    if (missingVehicle.length > 0) {
      toast.error(`Wybierz pojazd dla ${missingVehicle.length} kosztów (Paliwo/Eksploatacja/Leasing)`);
      return;
    }
    const payload = included.map((r) => ({
      date: r.sale_date,
      due_date: r.due_date || r.sale_date,
      type: r.type,
      category: r.category,
      amount: Number(r.gross_amount),
      net_amount: Number(r.net_amount),
      vat_rate: Number(r.vat_rate),
      vat_amount: Number(r.vat_amount),
      description: `${r.doc_type ? r.doc_type + " " : ""}${r.invoice_number} — ${r.contractor_name || ""} · ${r.description || ""}`.trim(),
      status: "actual",
      vehicle_id: r.vehicle_id || null,
      employee_id: r.employee_id || null,
    }));
    setCommitting(true);
    try {
      const res = await Finance.importCommit(payload);
      toast.success(`Zaimportowano ${res.created} transakcji`);
      reset();
      onClose();
      onImported?.();
    } catch (err) {
      toast.error("Błąd zapisu: " + (err.response?.data?.detail || err.message));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4"
      data-testid="import-modal"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="bg-white w-full max-w-6xl max-h-[90vh] flex flex-col border-2 border-zinc-900 shadow-[8px_8px_0_0_#18181b]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900 bg-zinc-50">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            <div>
              <div className="font-label text-xs">// Import z arkusza Excel</div>
              <h2 className="font-heading font-black text-lg uppercase tracking-tight">
                Import faktur (KUP + Przychody)
              </h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            data-testid="import-close"
            className="p-2 hover:bg-zinc-200"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {rows.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <label className="cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                data-testid="import-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <div className="border-2 border-dashed border-zinc-300 hover:border-zinc-900 p-16 text-center transition-colors">
                <Upload className="w-12 h-12 mx-auto text-zinc-400 mb-4" />
                <div className="font-heading font-bold text-xl mb-2">
                  {parsing ? "Analizuję plik..." : "Wybierz plik .xlsx"}
                </div>
                <div className="text-sm text-zinc-600 max-w-md mx-auto">
                  Zaakceptowane kolumny: Data sprzedaży, Numer faktury, Identyfikator/NIP,
                  Kontrahent, Opis, Stawka VAT, Kwota netto/VAT/brutto. Zwykle: KSeF export
                  z Fakturownia / Comarch / iFirma / SubiektGT.
                </div>
              </div>
            </label>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs" data-testid="import-rows-table">
                <thead className="bg-zinc-900 text-white sticky top-0 z-10">
                  <tr>
                    <Th>#</Th>
                    <Th>Data</Th>
                    <Th>Faktura</Th>
                    <Th>Kontrahent</Th>
                    <Th className="text-right">Netto</Th>
                    <Th className="text-right">VAT%</Th>
                    <Th className="text-right">Brutto</Th>
                    <Th>Typ</Th>
                    <Th>Kategoria</Th>
                    <Th>Pojazd / Pracownik</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const isIncome = r.type === "income";
                    const cats = isIncome ? meta.income_categories : meta.expense_categories;
                    const needsVehicle =
                      !isIncome && meta.categories_with_vehicle.includes(r.category);
                    const needsEmployee =
                      !isIncome && meta.categories_with_employee.includes(r.category);
                    return (
                      <tr
                        key={i}
                        data-testid={`import-row-${i}`}
                        className={`border-b border-zinc-200 ${
                          !r._include ? "opacity-40 bg-zinc-50" : "hover:bg-zinc-50"
                        }`}
                      >
                        <td className="px-3 py-2 font-mono">
                          <input
                            type="checkbox"
                            checked={r._include}
                            onChange={(e) => updateRow(i, { _include: e.target.checked })}
                            data-testid={`import-include-${i}`}
                            className="w-4 h-4 accent-emerald-600"
                          />
                        </td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{r.sale_date}</td>
                        <td className="px-3 py-2 font-mono max-w-[140px] truncate" title={r.invoice_number}>
                          {r.invoice_number}
                        </td>
                        <td className="px-3 py-2 max-w-[180px] truncate" title={r.contractor_name}>
                          <div>{r.contractor_name || "—"}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{r.contractor_id}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-right">
                          {r.net_amount.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 font-mono text-right">{r.vat_rate}%</td>
                        <td className="px-3 py-2 font-mono text-right font-bold">
                          {r.gross_amount.toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={r.type}
                            onChange={(e) => {
                              const newType = e.target.value;
                              const defaultCat = newType === "income" ? "Faktura sprzedaży" : "Inne koszty";
                              updateRow(i, {
                                type: newType,
                                category: defaultCat,
                                vehicle_id: "",
                                employee_id: "",
                              });
                            }}
                            data-testid={`import-type-${i}`}
                            className={`w-full border px-2 py-1 text-xs font-bold uppercase ${
                              isIncome
                                ? "bg-emerald-50 border-emerald-600 text-emerald-800"
                                : "bg-red-50 border-red-600 text-red-800"
                            }`}
                          >
                            <option value="income">Przychód</option>
                            <option value="expense">KUP</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={r.category || ""}
                            onChange={(e) =>
                              updateRow(i, {
                                category: e.target.value,
                                vehicle_id: "",
                                employee_id: "",
                              })
                            }
                            data-testid={`import-category-${i}`}
                            className="w-full border border-zinc-300 px-2 py-1 text-xs"
                          >
                            <option value="">— wybierz —</option>
                            {cats.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          {needsVehicle && (
                            <select
                              value={r.vehicle_id || ""}
                              onChange={(e) => updateRow(i, { vehicle_id: e.target.value })}
                              data-testid={`import-vehicle-${i}`}
                              className="w-full border border-zinc-300 px-2 py-1 text-xs"
                            >
                              <option value="">Pojazd —</option>
                              {fleet.map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.brand} {v.model} · {v.registration}
                                </option>
                              ))}
                            </select>
                          )}
                          {needsEmployee && (
                            <select
                              value={r.employee_id || ""}
                              onChange={(e) => updateRow(i, { employee_id: e.target.value })}
                              data-testid={`import-employee-${i}`}
                              className="w-full border border-zinc-300 px-2 py-1 text-xs"
                            >
                              <option value="">Pracownik —</option>
                              {employees.map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                  {emp.first_name} {emp.last_name}
                                </option>
                              ))}
                            </select>
                          )}
                          {r.warning && (
                            <div className="flex items-center gap-1 text-amber-700 text-[10px]">
                              <AlertTriangle className="w-3 h-3" /> {r.warning}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => updateRow(i, { _include: !r._include })}
                            title={r._include ? "Pomiń wiersz" : "Włącz wiersz"}
                            data-testid={`import-toggle-${i}`}
                            className="p-1 hover:bg-zinc-200"
                          >
                            <Trash2 className="w-3 h-3 text-zinc-500" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="border-t border-zinc-900 bg-zinc-50 px-6 py-4 flex items-center justify-between gap-4">
              <div className="text-sm">
                <div>
                  <span className="font-label">// Do importu:</span>{" "}
                  <span className="font-bold" data-testid="import-summary-count">
                    {included.length} z {rows.length}
                  </span>
                </div>
                <div className="text-xs text-zinc-600 font-mono">
                  Razem brutto: {totalGross.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
                >
                  Anuluj
                </button>
                <button
                  onClick={commit}
                  disabled={committing || included.length === 0}
                  data-testid="import-commit-btn"
                  className="inline-flex items-center gap-2 px-6 py-2 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {committing ? "Zapisuję..." : `Zaimportuj ${included.length}`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}
